import { useEffect, useState, useCallback, useMemo } from 'react';
import Select from 'react-select';
import {
  Modal, ModalHeader, ModalBody, ModalFooter,
  Form, FormGroup, Label, Input, Button, Row, Col,
  Collapse, Card, CardHeader, CardBody,
  InputGroup
} from 'reactstrap';
import { useDispatch } from 'react-redux';
import Swal from 'sweetalert2';
import jwt_decode from 'jwt-decode';
import { getRequestTypes } from '../Helpers/Api';
import { countries } from '../Constants/countries';
import { FileUploader } from '../Components/FileUploader';

const AddEditRMVendorModal = ({
  mode,
  open,
  onClose,
  formik,
  properties,
}) => {
  console.log({ formik })

  const dispatch = useDispatch();
  const [requestTypes, setRequestTypes] = useState([]);
  const [openAccordion, setOpenAccordion] = useState('company');

  const isAddMode = mode === 'add';
  const isEditMode = mode === 'edit';

  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      borderColor: state.isFocused ? '#007bff' : '#ced4da',
      boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(0, 123, 255, 0.25)' : 'none',
      '&:hover': { borderColor: '#007bff' },
      minHeight: '38px',
      fontSize: '14px',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#007bff' : state.isFocused ? '#f8f9fa' : 'white',
      color: state.isSelected ? 'white' : '#495057',
      '&:hover': { backgroundColor: state.isSelected ? '#007bff' : '#e9ecef' },
    }),
    placeholder: (provided) => ({ ...provided, color: '#6c757d' }),
    singleValue: (provided) => ({ ...provided, color: '#495057' }),
  };

  const propertyOptions = useMemo(() =>
    properties.map((prop) => ({
      value: prop._id,
      label: `${prop.title}, ${prop.location}`,
    })),
    [properties]
  );

  const getUserFromToken = useCallback(() => {
    try {
      const token = localStorage.getItem('accessToken');
      return token ? jwt_decode(token) : null;
    } catch {
      return null;
    }
  }, []);

  const fetchRequestTypes = useCallback(
    async (signal) => {
      try {
        const user = getUserFromToken();
        if (!user) return;
        dispatch({ type: 'SET_LOADING', payload: true });
        const data = await getRequestTypes(user?.company_id || user?.id || '', { signal });
        if (!signal.aborted) {
          setRequestTypes(data);
        }
      } catch (error) {
        if (!signal.aborted) {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: error.message,
            confirmButtonColor: '#dc3545',
          });
        }
      } finally {
        if (!signal.aborted) {
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      }
    },
    [dispatch, getUserFromToken]
  );

  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();

    if (open) {
      fetchRequestTypes(abortController.signal);
      setOpenAccordion('company');
    }

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [open, fetchRequestTypes]);

  const toggleAccordion = (id) => {
    setOpenAccordion(openAccordion === id ? '' : id);
  };

  const handleAddItem = () => {
    const newItems = [
      ...(formik.values.items || []),
      {
        propertyId: '',
        requestsSupported: '',
        make: '',
        modelNo: '',
        sku: '',
        images: [],
        costPerUnit: '',
        description: '',
        quantity: { min: '', max: '' },
      },
    ];
    formik.setFieldValue('items', newItems);
  };

  const handleRemoveItem = (index) => {
    const currentItems = formik.values.items || [];
    if (currentItems.length <= 1) return;

    const newItems = currentItems.filter((_, i) => i !== index);
    formik.setFieldValue('items', newItems);
  };

  const handleItemFieldChange = (itemIndex, fieldName) => (e) => {
    const { value } = e.target;
    formik.setFieldValue(`items[${itemIndex}].${fieldName}`, value);
  };

  const handleItemSelectChange = (itemIndex, fieldName) => (selectedOption) => {
    formik.setFieldValue(`items[${itemIndex}].${fieldName}`, selectedOption ? selectedOption.value : '');
  };

  const handleNestedItemFieldChange = (itemIndex, nestedField, field) => (e) => {
    const { value } = e.target;
    formik.setFieldValue(`items[${itemIndex}].${nestedField}.${field}`, value);
  };

  const getFieldError = (fieldPath) => {
    const fieldParts = fieldPath.split('.');
    let error = formik.errors;
    let touched = formik.touched;

    for (const part of fieldParts) {
      const match = part.match(/^(\w+)\[(\d+)\]$/);
      if (match) {
        const fieldName = match[1];
        const index = parseInt(match[2], 10);
        error = error?.[fieldName]?.[index];
        touched = touched?.[fieldName]?.[index];
      } else {
        error = error?.[part];
        touched = touched?.[part];
      }
      if (!error || !touched) return null;
    }
    return touched && error ? error : null;
  };

  const hasCompanyErrors = useMemo(() => {
    const companyFields = [
      'companyName',
      'contactName',
      'email',
      'phone.countryCode',
      'phone.number',
      'requestsSupported',
      'password',
      'address.street',
      'address.city',
      'address.province',
      'address.postalCode',
      'address.country',
    ];
    return companyFields.some((field) => getFieldError(field));
  }, [formik.errors, formik.touched]);

  const hasItemsErrors = useMemo(() => {
    const items = formik.values.items || [];
    return items.some((_, itemIndex) => {
      const itemFields = [
        `items.${itemIndex}.propertyId`,
        `items.${itemIndex}.requestsSupported`,
        `items.${itemIndex}.make`,
        `items.${itemIndex}.modelNo`,
        `items.${itemIndex}.sku`,
        `items.${itemIndex}.costPerUnit`,
        `items.${itemIndex}.description`,
        `items.${itemIndex}.quantity.min`,
        `items.${itemIndex}.quantity.max`,
      ];
      const imageFields = (formik.values.items[itemIndex]?.images || []).map(
        (_, imgIndex) => `items.${itemIndex}.images[${imgIndex}].url`
      );
      return [...itemFields, ...imageFields].some((field) => getFieldError(field));
    });
  }, [formik.errors, formik.touched, formik.values.items]);

  const items = formik.values.items || [];

  useEffect(() => {
    if (isEditMode && formik.values.items) {
      const updatedItems = formik.values.items.map((item) => ({
        ...item,
        requestsSupported: formik.values.requestsSupported.includes(item.requestsSupported) ? item.requestsSupported : '',
      }));
      formik.setFieldValue('items', updatedItems);
    }
  }, [formik.values.requestsSupported, isEditMode]);

  return (
    <Modal
      isOpen={open}
      toggle={onClose}
      size="xl"
      className="vendor-modal"
      style={{ maxWidth: '95%', width: '95%', margin: '2rem auto', height: '90vh', maxHeight: '90vh' }}
    >
      <ModalHeader toggle={onClose} className="border-bottom">
        <div className="d-flex align-items-center">
          <i className="fas fa-building me-2 text-primary"></i>
          <span>{isEditMode ? 'Edit RM Vendor' : 'Add RM Vendor'}</span>
        </div>
      </ModalHeader>

      <Form onSubmit={formik.handleSubmit}>
        <ModalBody className="p-4" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
          <Card className="border-0 shadow-sm mb-3">
            <CardHeader
              className={`border-bottom d-flex justify-content-between align-items-center ${hasCompanyErrors ? 'bg-danger bg-opacity-10' : 'bg-light'}`}
              style={{ cursor: 'pointer' }}
              onClick={() => toggleAccordion('company')}
            >
              <h6 className="mb-0 text-dark">
                <i className="fas fa-building me-2 text-primary"></i>
                Company Information
              </h6>
              <span>
                <i className={`fas ${openAccordion === 'company' ? 'fa-chevron-up' : 'fa-chevron-down'} me-2`}></i>
              </span>
            </CardHeader>
            <Collapse isOpen={openAccordion === 'company'}>
              <CardBody className="p-4">
                <Row className="mb-3 g-3">
                  <Col md={2}>
                    <FormGroup>
                      <Label className="form-label fw-semibold">
                        <i className="fas fa-building me-2 text-primary"></i>
                        Company Name *
                      </Label>
                      <Input
                        id="companyName"
                        name="companyName"
                        value={formik.values.companyName || ''}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        disabled={formik.isSubmitting}
                        invalid={!!(formik.touched.companyName && formik.errors.companyName)}
                        required
                        className="form-control"
                      />
                      {formik.touched.companyName && formik.errors.companyName && (
                        <div className="text-danger small mt-1">
                          <i className="fas fa-exclamation-circle me-1"></i>
                          {formik.errors.companyName}
                        </div>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <Label className="form-label fw-semibold">
                        <i className="fas fa-user me-2 text-primary"></i>
                        Contact Name *
                      </Label>
                      <Input
                        id="contactName"
                        name="contactName"
                        value={formik.values.contactName || ''}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        disabled={formik.isSubmitting}
                        invalid={!!(formik.touched.contactName && formik.errors.contactName)}
                        required
                        className="form-control"
                      />
                      {formik.touched.contactName && formik.errors.contactName && (
                        <div className="text-danger small mt-1">
                          <i className="fas fa-exclamation-circle me-1"></i>
                          {formik.errors.contactName}
                        </div>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <Label className="form-label fw-semibold">
                        <i className="fas fa-envelope me-2 text-primary"></i>
                        Email *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formik.values.email || ''}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        disabled={formik.isSubmitting}
                        invalid={!!(formik.touched.email && formik.errors.email)}
                        required
                        className="form-control"
                      />
                      {formik.touched.email && formik.errors.email && (
                        <div className="text-danger small mt-1">
                          <i className="fas fa-exclamation-circle me-1"></i>
                          {formik.errors.email}
                        </div>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <Label className="form-label fw-semibold">
                        <i className="fas fa-phone me-2 text-primary"></i>
                        Phone Number *
                      </Label>
                      <InputGroup>
                        <Input
                          id="phone.countryCode"
                          name="phone.countryCode"
                          value={formik.values.phone?.countryCode || ''}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          disabled={formik.isSubmitting}
                          invalid={!!(formik.touched.phone?.countryCode && formik.errors.phone?.countryCode)}
                          required
                          style={{ maxWidth: '80px' }}
                          placeholder="+1"
                          className="form-control"
                        />
                        <Input
                          id="phone.number"
                          name="phone.number"
                          value={formik.values.phone?.number || ''}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          disabled={formik.isSubmitting}
                          invalid={!!(formik.touched.phone?.number && formik.errors.phone?.number)}
                          required
                          placeholder="Enter phone number"
                          className="form-control"
                        />
                      </InputGroup>
                      {(formik.touched.phone?.countryCode && formik.errors.phone?.countryCode) ||
                        (formik.touched.phone?.number && formik.errors.phone?.number) ? (
                        <div className="text-danger small mt-1">
                          <i className="fas fa-exclamation-circle me-1"></i>
                          {formik.errors.phone?.countryCode || formik.errors.phone?.number}
                        </div>
                      ) : null}
                    </FormGroup>
                  </Col>
                  <Col md={isAddMode ? 2 : 4}>
                    <FormGroup>
                      <Label className="form-label fw-semibold">
                        <i className="fas fa-list me-2 text-primary"></i>
                        Supported Request Types *
                      </Label>
                      <Select
                        id="requestsSupported"
                        name="requestsSupported"
                        isMulti
                        value={(formik.values.requestsSupported || [])
                          .map((id) => {
                            const type = requestTypes.find((t) => t._id === id);
                            return type ? { value: type._id, label: type.request_type } : null;
                          })
                          .filter(Boolean)}
                        onChange={(selectedOptions) => {
                          const ids = selectedOptions.map((option) => option.value);
                          formik.setFieldValue('requestsSupported', ids);
                        }}
                        onBlur={() => formik.setFieldTouched('requestsSupported', true)}
                        options={requestTypes.map((type) => ({
                          value: type._id,
                          label: type.request_type,
                        }))}
                        placeholder="Select request types"
                        isDisabled={formik.isSubmitting}
                        classNamePrefix="react-select"
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                      />
                      {formik.touched.requestsSupported && formik.errors.requestsSupported && (
                        <div className="text-danger small mt-1">
                          <i className="fas fa-exclamation-circle me-1"></i>
                          {formik.errors.requestsSupported}
                        </div>
                      )}
                    </FormGroup>
                  </Col>
                  {isAddMode && (
                    <Col md={2}>
                      <FormGroup>
                        <Label className="form-label fw-semibold">
                          <i className="fas fa-lock me-2 text-primary"></i>
                          Password
                        </Label>
                        <Input
                          id="password"
                          name="password"
                          type="password"
                          value={formik.values.password || ''}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          disabled={formik.isSubmitting}
                          invalid={!!(formik.touched.password && formik.errors.password)}
                          className="form-control"
                        />
                        {formik.touched.password && formik.errors.password && (
                          <div className="text-danger small mt-1">
                            <i className="fas fa-exclamation-circle me-1"></i>
                            {formik.errors.password}
                          </div>
                        )}
                      </FormGroup>
                    </Col>
                  )}
                </Row>

                <h6 className="mb-3 text-dark fw-semibold">
                  <i className="fas fa-map-marker-alt me-2 text-primary"></i>
                  Address
                </h6>
                <Row className="g-3">
                  <Col md={4}>
                    <FormGroup>
                      <Label className="form-label fw-semibold">
                        <i className="fas fa-road me-2 text-primary"></i>
                        Street
                      </Label>
                      <Input
                        id="address.street"
                        name="address.street"
                        value={formik.values.address?.street || ''}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        disabled={formik.isSubmitting}
                        invalid={!!(formik.touched.address?.street && formik.errors.address?.street)}
                        className="form-control"
                      />
                      {formik.touched.address?.street && formik.errors.address?.street && (
                        <div className="text-danger small mt-1">
                          <i className="fas fa-exclamation-circle me-1"></i>
                          {formik.errors.address.street}
                        </div>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <Label className="form-label fw-semibold">
                        <i className="fas fa-city me-2 text-primary"></i>
                        City
                      </Label>
                      <Input
                        id="address.city"
                        name="address.city"
                        value={formik.values.address?.city || ''}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        disabled={formik.isSubmitting}
                        invalid={!!(formik.touched.address?.city && formik.errors.address?.city)}
                        className="form-control"
                      />
                      {formik.touched.address?.city && formik.errors.address?.city && (
                        <div className="text-danger small mt-1">
                          <i className="fas fa-exclamation-circle me-1"></i>
                          {formik.errors.address.city}
                        </div>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <Label className="form-label fw-semibold">
                        <i className="fas fa-map me-2 text-primary"></i>
                        Province
                      </Label>
                      <Input
                        id="address.province"
                        name="address.province"
                        value={formik.values.address?.province || ''}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        disabled={formik.isSubmitting}
                        invalid={!!(formik.touched.address?.province && formik.errors.address?.province)}
                        className="form-control"
                      />
                      {formik.touched.address?.province && formik.errors.address?.province && (
                        <div className="text-danger small mt-1">
                          <i className="fas fa-exclamation-circle me-1"></i>
                          {formik.errors.address.province}
                        </div>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <Label className="form-label fw-semibold">
                        <i className="fas fa-mail-bulk me-2 text-primary"></i>
                        Postal Code
                      </Label>
                      <Input
                        id="address.postalCode"
                        name="address.postalCode"
                        value={formik.values.address?.postalCode || ''}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        disabled={formik.isSubmitting}
                        invalid={!!(formik.touched.address?.postalCode && formik.errors.address?.postalCode)}
                        className="form-control"
                      />
                      {formik.touched.address?.postalCode && formik.errors.address?.postalCode && (
                        <div className="text-danger small mt-1">
                          <i className="fas fa-exclamation-circle me-1"></i>
                          {formik.errors.address.postalCode}
                        </div>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <Label className="form-label fw-semibold">
                        <i className="fas fa-globe me-2 text-primary"></i>
                        Country
                      </Label>
                      <Select
                        id="address.country"
                        name="address.country"
                        value={
                          countries
                            .map((c) => ({ value: c.isoCode, label: `${c.flag} ${c.name}` }))
                            .find((option) => option.value === formik.values.address?.country) || null
                        }
                        onChange={(selectedOption) =>
                          formik.setFieldValue('address.country', selectedOption ? selectedOption.value : '')
                        }
                        onBlur={() => formik.setFieldTouched('address.country', true)}
                        options={countries.map((c) => ({
                          value: c.isoCode,
                          label: `${c.flag} ${c.name}`,
                        }))}
                        placeholder="Select Country"
                        isDisabled={formik.isSubmitting}
                        classNamePrefix="react-select"
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                      />
                      {formik.touched.address?.country && formik.errors.address?.country && (
                        <div className="text-danger small mt-1">
                          <i className="fas fa-exclamation-circle me-1"></i>
                          {formik.errors.address.country}
                        </div>
                      )}
                    </FormGroup>
                  </Col>
                </Row>
              </CardBody>
            </Collapse>
          </Card>

          <Card className="border-0 shadow-sm mb-3">
            <CardHeader
              className={`border-bottom d-flex justify-content-between align-items-center ${hasItemsErrors ? 'bg-danger bg-opacity-10' : 'bg-light'}`}
              style={{ cursor: 'pointer' }}
              onClick={() => toggleAccordion('items')}
            >
              <h6 className="mb-0 text-dark">
                <i className="fas fa-cubes me-2 text-primary"></i>
                Items ({items.length})
              </h6>
              <Button
                type="button"
                color="success"
                size="sm"
                className="me-2 px-3 py-1"
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddItem();
                }}
                disabled={formik.isSubmitting}
              >
                <i className="fas fa-plus me-1"></i>
                Add Item
              </Button>
              <span>
                <i className={`fas ${openAccordion === 'items' ? 'fa-chevron-up' : 'fa-chevron-down'} me-2`}></i>
              </span>
            </CardHeader>
            <Collapse isOpen={openAccordion === 'items'}>
              <CardBody className="p-4">
                {items.length === 0 ? (
                  <div className="text-center py-4 bg-light rounded">
                    <p className="mb-3 text-muted">No items added yet</p>
                    <Button
                      type="button"
                      color="success"
                      className="px-4 py-2"
                      onClick={handleAddItem}
                      disabled={formik.isSubmitting}
                    >
                      <i className="fas fa-plus me-2"></i>
                      Add First Item
                    </Button>
                  </div>
                ) : (
                  <div className="items-container">
                    {items.map((item, itemIndex) => (
                      <Card key={itemIndex} className="mb-3 border-0 shadow-sm">
                        <CardHeader className="bg-light border-bottom d-flex justify-content-between align-items-center py-2">
                          <h6 className="mb-0 text-dark" style={{ fontSize: '0.875rem' }}>
                            <i className="fas fa-cube me-2 text-primary"></i>
                            Item {itemIndex + 1}
                            {item.make && ` - ${item.make}`}
                            {item.modelNo && ` (${item.modelNo})`}
                          </h6>
                          <Button
                            type="button"
                            color="danger"
                            size="sm"
                            className="px-3 py-1"
                            onClick={() => handleRemoveItem(itemIndex)}
                            disabled={formik.isSubmitting}
                          >
                            <i className="fas fa-trash me-1"></i>
                            Remove
                          </Button>
                        </CardHeader>
                        <CardBody className="p-3">
                          <Row className="mb-3 g-3">
                            <Col md={3}>
                              <FormGroup>
                                <Label className="form-label fw-semibold">
                                  <i className="fas fa-building me-2 text-primary"></i>
                                  Property *
                                </Label>
                                <Select
                                  value={propertyOptions.find((option) => option.value === item.propertyId) || null}
                                  onChange={handleItemSelectChange(itemIndex, 'propertyId')}
                                  onBlur={() => formik.setFieldTouched(`items[${itemIndex}].propertyId`, true)}
                                  options={propertyOptions}
                                  placeholder="Select Property"
                                  isDisabled={formik.isSubmitting}
                                  classNamePrefix="react-select"
                                  styles={selectStyles}
                                  menuPortalTarget={document.body}
                                />
                                {getFieldError(`items.${itemIndex}.propertyId`) && (
                                  <div className="text-danger small mt-1">
                                    <i className="fas fa-exclamation-circle me-1"></i>
                                    {getFieldError(`items.${itemIndex}.propertyId`)}
                                  </div>
                                )}
                              </FormGroup>
                            </Col>
                            <Col md={3}>
                              <FormGroup>
                                <Label className="form-label fw-semibold">
                                  <i className="fas fa-folder me-2 text-primary"></i>
                                  Request Type
                                </Label>
                                <Input
                                  type="select"
                                  name={`items[${itemIndex}].requestsSupported`}
                                  value={item.requestsSupported || ''}
                                  onChange={handleItemFieldChange(itemIndex, 'requestsSupported')}
                                  disabled={formik.isSubmitting}
                                  invalid={!!getFieldError(`items.${itemIndex}.requestsSupported`)}
                                  className="form-control"
                                >
                                  <option value="">Select Request Type</option>
                                  {requestTypes
                                    .filter((req) => formik.values.requestsSupported.includes(req._id))
                                    .map((req) => (
                                      <option key={req._id} value={req._id}>
                                        {req.request_type || req.name}
                                      </option>
                                    ))}
                                </Input>
                                {getFieldError(`items.${itemIndex}.requestsSupported`) && (
                                  <div className="text-danger small mt-1">
                                    <i className="fas fa-exclamation-circle me-1"></i>
                                    {getFieldError(`items.${itemIndex}.requestsSupported`)}
                                  </div>
                                )}
                              </FormGroup>
                            </Col>
                            <Col md={3}>
                              <FormGroup>
                                <Label className="form-label fw-semibold">
                                  <i className="fas fa-industry me-2 text-primary"></i>
                                  Make
                                </Label>
                                <Input
                                  name={`items[${itemIndex}].make`}
                                  value={item.make || ''}
                                  onChange={handleItemFieldChange(itemIndex, 'make')}
                                  disabled={formik.isSubmitting}
                                  invalid={!!getFieldError(`items.${itemIndex}.make`)}
                                  className="form-control"
                                />
                                {getFieldError(`items.${itemIndex}.make`) && (
                                  <div className="text-danger small mt-1">
                                    <i className="fas fa-exclamation-circle me-1"></i>
                                    {getFieldError(`items.${itemIndex}.make`)}
                                  </div>
                                )}
                              </FormGroup>
                            </Col>
                            <Col md={3}>
                              <FormGroup>
                                <Label className="form-label fw-semibold">
                                  <i className="fas fa-tag me-2 text-primary"></i>
                                  Model Number
                                </Label>
                                <Input
                                  name={`items[${itemIndex}].modelNo`}
                                  value={item.modelNo || ''}
                                  onChange={handleItemFieldChange(itemIndex, 'modelNo')}
                                  disabled={formik.isSubmitting}
                                  invalid={!!getFieldError(`items.${itemIndex}.modelNo`)}
                                  className="form-control"
                                />
                                {getFieldError(`items.${itemIndex}.modelNo`) && (
                                  <div className="text-danger small mt-1">
                                    <i className="fas fa-exclamation-circle me-1"></i>
                                    {getFieldError(`items.${itemIndex}.modelNo`)}
                                  </div>
                                )}
                              </FormGroup>
                            </Col>
                          </Row>

                          <Row className="mb-3 g-3">
                            <Col md={3}>
                              <FormGroup>
                                <Label className="form-label fw-semibold">
                                  <i className="fas fa-barcode me-2 text-primary"></i>
                                  SKU
                                </Label>
                                <Input
                                  name={`items[${itemIndex}].sku`}
                                  value={item.sku || ''}
                                  onChange={handleItemFieldChange(itemIndex, 'sku')}
                                  disabled={formik.isSubmitting}
                                  invalid={!!getFieldError(`items.${itemIndex}.sku`)}
                                  className="form-control"
                                />
                                {getFieldError(`items.${itemIndex}.sku`) && (
                                  <div className="text-danger small mt-1">
                                    <i className="fas fa-exclamation-circle me-1"></i>
                                    {getFieldError(`items.${itemIndex}.sku`)}
                                  </div>
                                )}
                              </FormGroup>
                            </Col>
                            <Col md={3}>
                              <FormGroup>
                                <Label className="form-label fw-semibold">
                                  <i className="fas fa-dollar-sign me-2 text-primary"></i>
                                  Cost Per Unit
                                </Label>
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  name={`items[${itemIndex}].costPerUnit`}
                                  value={item.costPerUnit || ''}
                                  onChange={handleItemFieldChange(itemIndex, 'costPerUnit')}
                                  disabled={formik.isSubmitting}
                                  invalid={!!getFieldError(`items.${itemIndex}.costPerUnit`)}
                                  className="form-control"
                                />
                                {getFieldError(`items.${itemIndex}.costPerUnit`) && (
                                  <div className="text-danger small mt-1">
                                    <i className="fas fa-exclamation-circle me-1"></i>
                                    {getFieldError(`items.${itemIndex}.costPerUnit`)}
                                  </div>
                                )}
                              </FormGroup>
                            </Col>
                            <Col md={3}>
                              <FormGroup>
                                <Label className="form-label fw-semibold">
                                  <i className="fas fa-minus-circle me-2 text-primary"></i>
                                  Min Quantity
                                </Label>
                                <Input
                                  type="number"
                                  min="0"
                                  name={`items[${itemIndex}].quantity.min`}
                                  value={item.quantity?.min || ''}
                                  onChange={handleNestedItemFieldChange(itemIndex, 'quantity', 'min')}
                                  disabled={formik.isSubmitting}
                                  invalid={!!getFieldError(`items.${itemIndex}.quantity.min`)}
                                  className="form-control"
                                />
                                {getFieldError(`items.${itemIndex}.quantity.min`) && (
                                  <div className="text-danger small mt-1">
                                    <i className="fas fa-exclamation-circle me-1"></i>
                                    {getFieldError(`items.${itemIndex}.quantity.min`)}
                                  </div>
                                )}
                              </FormGroup>
                            </Col>
                            <Col md={3}>
                              <FormGroup>
                                <Label className="form-label fw-semibold">
                                  <i className="fas fa-plus-circle me-2 text-primary"></i>
                                  Max Quantity
                                </Label>
                                <Input
                                  type="number"
                                  min="0"
                                  name={`items[${itemIndex}].quantity.max`}
                                  value={item.quantity?.max || ''}
                                  onChange={handleNestedItemFieldChange(itemIndex, 'quantity', 'max')}
                                  disabled={formik.isSubmitting}
                                  invalid={!!getFieldError(`items.${itemIndex}.quantity.max`)}
                                  className="form-control"
                                />
                                {getFieldError(`items.${itemIndex}.quantity.max`) && (
                                  <div className="text-danger small mt-1">
                                    <i className="fas fa-exclamation-circle me-1"></i>
                                    {getFieldError(`items.${itemIndex}.quantity.max`)}
                                  </div>
                                )}
                              </FormGroup>
                            </Col>
                          </Row>

                          <Row className="mb-3">
                            <Col md={12}>
                              <FormGroup>
                                <Label className="form-label fw-semibold">
                                  <i className="fas fa-file-alt me-2 text-primary"></i>
                                  Description
                                </Label>
                                <Input
                                  type="textarea"
                                  rows={3}
                                  name={`items[${itemIndex}].description`}
                                  value={item.description || ''}
                                  onChange={handleItemFieldChange(itemIndex, 'description')}
                                  disabled={formik.isSubmitting}
                                  invalid={!!getFieldError(`items.${itemIndex}.description`)}
                                  className="form-control"
                                />
                                {getFieldError(`items.${itemIndex}.description`) && (
                                  <div className="text-danger small mt-1">
                                    <i className="fas fa-exclamation-circle me-1"></i>
                                    {getFieldError(`items.${itemIndex}.description`)}
                                  </div>
                                )}
                              </FormGroup>
                            </Col>
                          </Row>

                          <Row className="mb-3">
                            <Col md={12}>
                              <FileUploader
                                formik={formik}
                                fieldName={`items[${itemIndex}].images`}
                                label="Item Images"
                                isDisabled={formik.isSubmitting}
                                acceptedFileTypes="image/jpeg,image/png,image/gif,image/webp"
                                onSuccess={(result) => {
                                  console.log(`Uploaded images for item ${itemIndex + 1}:`, result.fileUrls);
                                }}
                              />
                            </Col>
                          </Row>
                        </CardBody>
                      </Card>
                    ))}
                  </div>
                )}
              </CardBody>
            </Collapse>
          </Card>
        </ModalBody>

        <ModalFooter className="border-top bg-light">
          <Button type="submit" color="primary" disabled={formik.isSubmitting} className="px-4 py-2">
            <i className="fas fa-save me-2"></i>
            {isEditMode ? 'Update Vendor' : 'Save Vendor'}
          </Button>
          <Button
            type="button"
            color="secondary"
            onClick={onClose}
            disabled={formik.isSubmitting}
            className="px-4 py-2"
          >
            <i className="fas fa-times me-2"></i>
            Cancel
          </Button>
        </ModalFooter>
      </Form>

      <style jsx>{`
        .vendor-modal .modal-header {
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
        }
        .react-select-container .react-select__control {
          border-radius: 0.375rem;
          transition: all 0.15s ease-in-out;
        }
        .react-select-container .react-select__control:hover {
          border-color: #007bff;
        }
        .react-select-container .react-select__menu {
          border-radius: 0.375rem;
          box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
        }
        .swal2-popup-custom {
          border-radius: 0.5rem;
        }
        .swal2-title-custom {
          color: #495057;
        }
        .form-label {
          margin-bottom: 0.5rem;
          color: #495057;
        }
        .card {
          transition: box-shadow 0.15s ease-in-out;
        }
        .card:hover {
          box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.1) !important;
        }
        .form-control {
          transition: all 0.15s ease-in-out;
        }
        .form-control:focus {
          border-color: #007bff;
          box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
        }
      `}</style>
    </Modal>
  );
};

export { AddEditRMVendorModal };